import {
  KEYCLOAK_CONFIG,
  KEYCLOAK_ENDPOINTS,
  isClientAuthenticationEnabled,
} from "@/constants/keycloak";
import {
  CallbackParams,
  KeycloakTokenResponse,
  KeycloakUserInfo,
} from "@/types/auth";

export class KeycloakService {
  private static readonly TOKEN_KEY = "keycloak_access_token";
  private static readonly USER_KEY = "keycloak_user_info";

  /**
   * Gera a URL de autorização do Keycloak
   */
  static generateAuthUrl(): string {
    const params = new URLSearchParams({
      response_type: "code",
      client_id: KEYCLOAK_CONFIG.CLIENT_ID,
      redirect_uri: KEYCLOAK_CONFIG.REDIRECT_URI,
      scope: "openid profile email",
    });

    const authUrl = `${KEYCLOAK_ENDPOINTS.AUTHORIZATION}?${params.toString()}`;
    return authUrl;
  }

  /**
   * Redireciona para a tela de login do Keycloak
   */
  static redirectToLogin(): void {
    const authUrl = this.generateAuthUrl();
    window.location.href = authUrl;
  }

  /**
   * Extrai parâmetros da URL de callback
   */
  static extractCallbackParams(): CallbackParams {
    const urlParams = new URLSearchParams(window.location.search);
    const params: CallbackParams = {};

    for (const [key, value] of urlParams) {
      if (key === "code") params.code = value;
      if (key === "state") params.state = value;
      if (key === "error") params.error = value;
      if (key === "error_description") params.error_description = value;
    }

    return params;
  }

  /**
   * Troca o código de autorização por um access token
   */
  static async exchangeCodeForToken(
    code: string
  ): Promise<KeycloakTokenResponse> {
    const hasClientSecret = isClientAuthenticationEnabled();

    const formData = new URLSearchParams();
    formData.append("grant_type", "authorization_code");
    formData.append("client_id", KEYCLOAK_CONFIG.CLIENT_ID);

    // Adicionar client_secret apenas se disponível
    if (hasClientSecret) {
      formData.append("client_secret", KEYCLOAK_CONFIG.CLIENT_SECRET!);
    }

    formData.append("redirect_uri", KEYCLOAK_CONFIG.REDIRECT_URI);
    formData.append("code", code);

    try {
      const response = await fetch(KEYCLOAK_ENDPOINTS.TOKEN, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Erro ao obter token: ${response.status} - ${errorText}`
        );
      }

      const tokenData: KeycloakTokenResponse = await response.json();

      return tokenData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Armazena o access token no localStorage
   */
  static storeAccessToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Recupera o access token do localStorage
   */
  static getAccessToken(): string | null {
    const token = localStorage.getItem(this.TOKEN_KEY);
    return token;
  }

  /**
   * Remove o access token do localStorage
   */
  static removeAccessToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  /**
   * Verifica se existe um token válido
   */
  static hasValidToken(): boolean {
    const token = this.getAccessToken();
    const hasToken = !!token;
    return hasToken;
  }

  /**
   * Verifica se o token ainda é válido fazendo uma requisição para o Keycloak
   */
  static async validateToken(): Promise<boolean> {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    try {
      const response = await fetch(KEYCLOAK_ENDPOINTS.USER_INFO, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        return true;
      } else {
        // Token inválido, limpar dados locais
        this.removeAccessToken();
        return false;
      }
    } catch (error) {
      // Em caso de erro de rede, assumir que o token ainda é válido
      return true;
    }
  }

  /**
   * Obtém informações do usuário usando o access token
   */
  static async getUserInfo(): Promise<KeycloakUserInfo> {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error("Access token não encontrado");
    }

    try {
      const response = await fetch(KEYCLOAK_ENDPOINTS.USER_INFO, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Erro ao obter informações do usuário: ${response.status} - ${errorText}`
        );
      }

      const userInfo: KeycloakUserInfo = await response.json();

      return userInfo;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Armazena informações do usuário no localStorage
   */
  static storeUserInfo(userInfo: KeycloakUserInfo): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(userInfo));
  }

  /**
   * Recupera informações do usuário do localStorage
   */
  static getUserInfoFromStorage(): KeycloakUserInfo | null {
    const userInfoStr = localStorage.getItem(this.USER_KEY);
    if (!userInfoStr) {
      return null;
    }

    try {
      const userInfo = JSON.parse(userInfoStr);
      return userInfo;
    } catch (error) {
      return null;
    }
  }

  /**
   * Faz logout redirecionando para o Keycloak
   */
  static logout(): void {
    this.removeAccessToken();

    const logoutUrl = `${KEYCLOAK_ENDPOINTS.LOGOUT}?client_id=${KEYCLOAK_CONFIG.CLIENT_ID}`;
    window.location.href = logoutUrl;
  }
}
